import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Headphones, HeartPulse, Plane, Banknote, Truck, Building2, ArrowRight, Mic,
  Sparkles, ShieldCheck, Globe, Activity, LogIn, Bot, Ear, Route, Database,
  CheckCircle2, FileText, PhoneCall, Lock,
} from 'lucide-react';
import { AgentRuntime } from '../components/landing/AgentRuntime';

// ── Industry product lines (each with a human agent persona + problem/solution) ──
const PRODUCTS = [
  {
    key: 'telco', name: 'Vokentra Telco', persona: 'Aria', role: 'Telecom voice agent',
    live: true, icon: <Headphones className="w-5 h-5" />, from: '#e60000', to: '#ff6a3d',
    img: '/images/agents/telco.jpg',
    problem: 'Customers wait on hold for bill shocks, dropped signal, SIM-swaps and roaming worries — while agents read scripts that don’t actually answer the question.',
    solution: 'Aria verifies the caller, understands the real ask, checks live billing, network and SIM data, explains the cause, and resolves it or raises a ticket — all in natural conversation.',
    caps: ['Billing & refunds', 'Network & outages', 'SIM / eSIM', 'Roaming', 'Fraud / SIM-swap', 'Upgrades & retention'],
  },
  {
    key: 'care', name: 'Vokentra Care', persona: 'Caira', role: 'Healthcare voice agent',
    icon: <HeartPulse className="w-5 h-5" />, from: '#10b981', to: '#22d3ee',
    img: '/images/agents/care.jpg',
    problem: 'Clinics drown in appointment calls, prescription queries and triage — patients wait, and front-desk staff burn out.',
    solution: 'Caira books, reschedules and confirms appointments, answers prescription and prep questions, and safely escalates urgent or vulnerable patients to a human — with accessible, calm language.',
    caps: ['Appointment booking', 'Reminders', 'Prescription queries', 'Triage & escalation', 'Accessibility-first'],
  },
  {
    key: 'travel', name: 'Vokentra Travel', persona: 'Voya', role: 'Travel & roaming agent',
    icon: <Plane className="w-5 h-5" />, from: '#3b82f6', to: '#8b5cf6',
    img: '/images/agents/travel.jpg',
    problem: 'Travellers need fast answers on bookings, changes, delays and data abroad — across time zones and languages.',
    solution: 'Voya handles itinerary changes, disruption rebooking, baggage and roaming questions, and sends proactive delay alerts before customers even call.',
    caps: ['Bookings & changes', 'Disruption rebooking', 'Roaming & data', 'Loyalty', '24/7 multilingual'],
  },
  {
    key: 'finance', name: 'Vokentra Finance', persona: 'Fynn', role: 'Finance voice agent',
    icon: <Banknote className="w-5 h-5" />, from: '#f59e0b', to: '#eab308',
    img: '/images/agents/finance.jpg',
    problem: 'Banks and fintechs face card disputes, fraud alerts and KYC friction at scale — where one mistake is a compliance risk.',
    solution: 'Fynn handles balance and transaction queries, card freezes, dispute intake and fraud verification — with strict identity checks and a full audit trail on every action.',
    caps: ['Card controls', 'Disputes', 'Fraud verification', 'KYC assist', 'Compliance-grade logging'],
  },
  {
    key: 'logistics', name: 'Vokentra Logistics', persona: 'Lex', role: 'Logistics voice agent',
    icon: <Truck className="w-5 h-5" />, from: '#06b6d4', to: '#3b82f6',
    img: '/images/agents/logistics.jpg',
    problem: 'Shippers and carriers field endless “where’s my order?” calls and delivery reschedules that tie up the whole team.',
    solution: 'Lex gives live shipment status, reschedules deliveries, captures failed-delivery reasons and pushes ETA updates automatically — handing off to a depot or driver when needed.',
    caps: ['Track & trace', 'Reschedule', 'Proof of delivery', 'Driver/depot handoff', 'Proactive ETA alerts'],
  },
  {
    key: 'enterprise', name: 'Vokentra Enterprise', persona: 'Enzo', role: 'Enterprise service agent',
    icon: <Building2 className="w-5 h-5" />, from: '#a855f7', to: '#ec4899',
    img: '/images/agents/enterprise.jpg',
    problem: 'Internal IT, HR and facilities desks are overwhelmed by repetitive employee requests — under constant SLA pressure.',
    solution: 'Enzo resolves IT/HR/service requests, checks SLAs, raises and tracks tickets, retrieves the right knowledge, and routes everything else to the right team.',
    caps: ['IT/HR service desk', 'SLA tracking', 'Ticketing', 'Knowledge retrieval', 'SSO ready'],
  },
];

const STATS = [
  { v: '6', l: 'industry product lines' },
  { v: '19', l: 'specialist agents' },
  { v: '<1s', l: 'intent routing' },
  { v: '100%', l: 'answers grounded in data' },
];

const STEPS = [
  { icon: <Ear className="w-5 h-5" />, t: 'Listen', d: 'Natural speech in — real-time transcription.' },
  { icon: <Bot className="w-5 h-5" />, t: 'Understand', d: 'Detects the real intent, not just keywords.' },
  { icon: <Route className="w-5 h-5" />, t: 'Route', d: 'Sends the call to the right specialist agent.' },
  { icon: <Database className="w-5 h-5" />, t: 'Check', d: 'Pulls live billing, network & account data.' },
  { icon: <CheckCircle2 className="w-5 h-5" />, t: 'Resolve', d: 'Explains the cause and acts — or escalates.' },
  { icon: <FileText className="w-5 h-5" />, t: 'Summarise', d: 'Structured post-call summary & ticket.' },
];

function Portrait({ p }: { p: typeof PRODUCTS[number] }) {
  return (
    <div className="relative inline-block">
      <div className="absolute -inset-4 rounded-[2rem] blur-2xl opacity-30"
           style={{ background: `radial-gradient(circle, ${p.to}, transparent 70%)` }} />
      <div className="relative w-52 h-64 sm:w-64 sm:h-72 rounded-[1.75rem] p-1 shadow-2xl"
           style={{ background: `linear-gradient(150deg, ${p.from}, ${p.to})` }}>
        <img src={`${p.img}?v=3`} alt={`${p.persona} — ${p.role}`} loading="lazy"
             onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0'; }}
             className="w-full h-full object-cover rounded-[1.5rem]" />
        {/* persona chip */}
        <div className="absolute left-3 bottom-3 right-3 backdrop-blur-md bg-black/40 rounded-xl px-3 py-2 flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: p.to }}>{p.icon}</span>
          <div className="leading-tight">
            <div className="text-white text-sm font-semibold">{p.persona}</div>
            <div className="text-white/70 text-[10px]">{p.role}</div>
          </div>
          <span className="ml-auto w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function IndustrySection({ p, i }: { p: typeof PRODUCTS[number]; i: number }) {
  const reverse = i % 2 === 1;
  return (
    <section className="py-12 px-6">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.5 }} className={`flex justify-center ${reverse ? 'md:order-2' : ''}`}>
          <Portrait p={p} />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }} className={reverse ? 'md:order-1' : ''}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${p.from}22`, color: p.to }}>{p.name}</span>
            {p.live && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-success/15 text-success">LIVE NOW</span>}
          </div>
          <h3 className="text-2xl font-bold text-text-primary">{p.role}</h3>

          <div className="mt-4 space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-danger/80 font-semibold mb-1">The problem</div>
              <p className="text-sm text-text-secondary leading-relaxed">{p.problem}</p>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-success/80 font-semibold mb-1">The Vokentra solution</div>
              <p className="text-sm text-text-secondary leading-relaxed">{p.solution}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {p.caps.map((c) => (
              <span key={c} className="text-[11px] px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/[0.07] text-text-secondary">{c}</span>
            ))}
          </div>

          <div className="mt-5">
            {p.live ? (
              <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-vokentra text-white text-sm font-medium hover:bg-vokentra-dark transition-colors shadow-lg shadow-vokentra/30">
                <Mic className="w-4 h-4" /> Try the Telco agent
              </Link>
            ) : (
              <Link to="/company" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/[0.12] text-text-primary text-sm font-medium hover:bg-white/[0.04] transition-colors">
                Learn more <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-vokentra flex items-center justify-center shadow-lg shadow-vokentra/30">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="white" />
              <circle cx="12" cy="12" r="5" fill="white" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold text-text-primary">Vokentra AI</div>
            <div className="text-[10px] text-text-muted">Voice Agents Platform</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-text-secondary">
          <a href="#products" className="hover:text-text-primary transition-colors">Products</a>
          <a href="#how" className="hover:text-text-primary transition-colors">How it works</a>
          <a href="#industries" className="hover:text-text-primary transition-colors">Industries</a>
          <Link to="/company" className="hover:text-text-primary transition-colors">Company</Link>
        </nav>
        <Link to="/login" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-vokentra text-white text-sm font-medium hover:bg-vokentra-dark transition-colors shadow-lg shadow-vokentra/30">
          <LogIn className="w-4 h-4" /> Login
        </Link>
      </div>
    </header>
  );
}

export function Landing() {
  return (
    <div className="min-h-screen bg-background text-text-primary overflow-x-hidden">
      <TopNav />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-grid-pattern opacity-40" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% -10%, rgba(230,0,0,0.16) 0%, transparent 60%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 50% 50% at 82% 25%, rgba(139,92,246,0.12) 0%, transparent 60%)' }} />
        </div>

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.1] bg-white/[0.03] text-xs text-text-secondary mb-6">
              <Sparkles className="w-3.5 h-3.5 text-vokentra" /> Multi-agent voice intelligence
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Intelligent voice agents for{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(120deg,#ff6a3d,#e60000 45%,#a855f7)' }}>real-world operations</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-lg text-text-secondary max-w-xl mx-auto lg:mx-0">
              Vokentra AI resolves customer and operational problems across telecom, healthcare, travel,
              finance, logistics and enterprise — with specialist voice agents that listen, understand,
              check live systems, and act.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-vokentra text-white font-medium hover:bg-vokentra-dark transition-colors shadow-xl shadow-vokentra/30">
                <Mic className="w-4 h-4" /> Launch the console
              </Link>
              <Link to="/company" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.12] text-text-primary font-medium hover:bg-white/[0.04] transition-colors">
                Discover Vokentra <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <p className="mt-6 text-[11px] text-text-muted">A product of Big Data Science &amp; Technology Limited</p>
          </div>

          {/* Hero agent collage */}
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="relative hidden lg:flex justify-center">
            <div className="relative grid grid-cols-3 gap-3">
              {PRODUCTS.map((p, k) => (
                <motion.div key={p.key} animate={{ y: [0, k % 2 ? 9 : -9, 0] }} transition={{ duration: 4 + k * 0.6, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-28 h-36 rounded-2xl p-0.5 shadow-xl" style={{ background: `linear-gradient(150deg, ${p.from}, ${p.to})` }}>
                  <img src={`${p.img}?v=3`} alt={p.persona} loading="lazy"
                       onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0'; }}
                       className="w-full h-full object-cover rounded-[14px]" />
                </motion.div>
              ))}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 rounded-2xl bg-background/80 backdrop-blur border border-white/10 flex items-center justify-center shadow-2xl">
                  <Mic className="w-7 h-7 text-vokentra" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats band */}
      <section className="px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
          {STATS.map((s) => (
            <div key={s.l} className="text-center">
              <div className="text-3xl font-extrabold text-text-primary">{s.v}</div>
              <div className="text-[11px] text-text-muted mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Interactive agent runtime / builder */}
      <AgentRuntime />

      {/* Products by industry */}
      <section id="products" className="pt-20">
        <div className="text-center px-6 mb-4">
          <h2 className="text-3xl font-bold">A specialist voice agent for every industry</h2>
          <p className="text-text-muted mt-2 max-w-2xl mx-auto">Same platform, purpose-built agents. Each one is trained on its industry’s problems and connected to the systems that solve them.</p>
        </div>
        {PRODUCTS.map((p, i) => <IndustrySection key={p.key} p={p} i={i} />)}
      </section>

      {/* How it works */}
      <section id="how" className="px-6 py-16 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold">How a Vokentra call works</h2>
          <p className="text-text-muted mt-2">Every call follows the same grounded pipeline — never guessing, never hallucinating account data.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
              className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 text-center">
              <div className="w-10 h-10 mx-auto rounded-xl bg-vokentra/15 text-vokentra flex items-center justify-center mb-2">{s.icon}</div>
              <div className="text-[10px] text-text-muted">Step {i + 1}</div>
              <div className="font-semibold text-text-primary text-sm">{s.t}</div>
              <div className="text-[11px] text-text-muted mt-1 leading-snug">{s.d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust band */}
      <section className="px-6 py-12 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: <ShieldCheck className="w-6 h-6" />, t: 'Grounded & compliant', d: 'Identity verified before any account detail; answers only from live systems — no hallucinations.' },
            { icon: <Lock className="w-6 h-6" />, t: 'Secure by design', d: 'JWT-gated console, encrypted transport, service-to-service auth, and audit-ready logging.' },
            { icon: <Activity className="w-6 h-6" />, t: 'Observable', d: 'Live transcripts, per-call insight, recordings, evaluation scores and full tracing.' },
          ].map((f) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
              <div className="w-11 h-11 rounded-xl bg-vokentra/15 text-vokentra flex items-center justify-center mb-4">{f.icon}</div>
              <h3 className="font-semibold text-text-primary">{f.t}</h3>
              <p className="text-sm text-text-muted mt-2 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Industries strip */}
      <section id="industries" className="px-6 py-12 max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-text-muted text-sm mb-6"><Globe className="w-4 h-4" /> One platform, six industries</div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {PRODUCTS.map((p) => (
            <span key={p.key} className="px-4 py-2 rounded-full border border-white/[0.1] bg-white/[0.03] text-sm text-text-secondary inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: p.to }} /> {p.name}
            </span>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-20">
        <div className="max-w-4xl mx-auto rounded-3xl border border-white/[0.08] p-10 text-center relative overflow-hidden"
             style={{ background: 'radial-gradient(ellipse 80% 100% at 50% 0%, rgba(230,0,0,0.14), rgba(255,255,255,0.02))' }}>
          <h2 className="text-3xl font-bold">Put a voice agent on the job today</h2>
          <p className="text-text-secondary mt-3 max-w-xl mx-auto">Start with Vokentra Telco — the first agent in the Vokentra line — and resolve real customer calls in natural conversation.</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-vokentra text-white font-medium hover:bg-vokentra-dark transition-colors shadow-xl shadow-vokentra/30">
              <PhoneCall className="w-4 h-4" /> Login to the console
            </Link>
            <Link to="/company" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.12] text-text-primary font-medium hover:bg-white/[0.04] transition-colors">
              About Vokentra <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] px-6 py-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div>
            <div className="font-bold text-text-primary">Vokentra AI</div>
            <div className="text-text-muted text-xs">“Intelligent voice agents for real-world operations.”</div>
          </div>
          <div className="text-text-muted text-xs text-center sm:text-right">
            A product of <span className="text-text-secondary">Big Data Science &amp; Technology Limited</span><br />
            © {new Date().getFullYear()} Vokentra AI · <Link to="/company" className="hover:text-text-primary">Company</Link> · <Link to="/login" className="hover:text-text-primary">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
